<template>
  <div
    :class="[
      'app-banner px-6 py-6 md:px-10 md:py-6 items-center rounded-lg app-banner-max-w-sm app-banner-w-sm md:app-banner-max-w-lg md:app-banner-w-lg flex-col flex justify-start items-start',
      {
        'justify-center md:flex-row md:gap-8': inlineBanner,
        'md:flex-col': stackedBanner,
      },
      outerClass,
      bgClass,
    ]"
    :style="inlineStyle"
    :data-test-id="dataTestId"
  >
    <div :class="['flex items-center flex-col', { 'md:flex-col': stackedBanner, 'md:flex-row': inlineBanner }]">
      <div v-if="icon?.value?.src" class="w-12 h-12 md:mr-4 flex-shrink-0">
        <Image :media="icon" class="w-12 h-12" />
      </div>
      <div class="text-racq-dark-grey text-lg-bold text-center md:text-left">
        <RichText :field="{ value: title }" />
      </div>
      <div v-if="subTitle" class="text-racq-med-grey text-m md:text-base text-center md:text-left">
        <RichText :field="{ value: subTitle }" />
      </div>
    </div>

    <!-- Show add to wallet  -->
    <div v-if="showAddToWallet" class="flex flex-row flex-wrap items-center flex-shrink-0 pt-4 gap-4 justify-center">
      <div v-if="isAppleWalletEnabled && devicePlatform === 'apple'" class="m-auto flex">
        <button type="button" aria-label="Apple add to wallet icon" @click="() => handleWalletClick('apple')">
          <img :src="iconMap.appleWalletIcon" alt="Apple add to wallet icon" class="w-full" />
        </button>
      </div>
      <div
        v-if="isGoogleWalletEnabled && (devicePlatform === 'apple' || devicePlatform === 'android')"
        class="m-auto flex"
      >
        <button type="button" aria-label="Google add to wallet icon" @click="() => handleWalletClick('android')">
          <img :src="iconMap.googleWalletIcon" alt="Google add to wallet icon" class="w-full" />
        </button>
      </div>
      <div v-else class="flex justify-center pt-4">
        <Link class="text-m md:text-base" :field="addToWalletLinkDesktop" />
      </div>
    </div>

    <!-- App store download links -->
    <template v-if="showAppStoreButtons && (appleDownloadLink || googleDownloadLink)">
      <div
        :class="[
          'flex flex-row pt-4 items-center flex-shrink-0 gap-4',
          { 'justify-center': stackedBanner, 'md:pt-0': inlineBanner },
        ]"
      >
        <template v-if="appleDownloadLink">
          <a :href="appleDownloadLink" target="_blank" class="inline-block">
            <img src="@/assets/icons/apple-app-store-button.svg" alt="apple app store icon" class="inline-block" />
          </a>
        </template>

        <template v-if="googleDownloadLink">
          <a :href="googleDownloadLink" target="_blank" class="inline-block">
            <img src="@/assets/icons/google-play-store-button.svg" alt="google play store icon" class="inline-block" />
          </a>
        </template>
      </div>
    </template>

    <!-- Primary and Secondary buttons -->
    <template v-if="showPrimaryBtn || showSecondaryBtn">
      <div
        :class="[
          'flex flex-row pt-4 items-center flex-shrink-0 gap-4',
          { 'justify-center': stackedBanner, 'md:pt-0': inlineBanner },
        ]"
      >
        <template v-if="showPrimaryBtn">
          <Link
            :aria-label="primaryBtn?.value?.title"
            :field="primaryBtn"
            class="flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm btn-primary"
            @click="emitPrimaryClick"
          />
        </template>
        <template v-if="showSecondaryBtn">
          <Link
            :aria-label="secondaryBtn?.value?.title"
            :field="secondaryBtn"
            class="flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm btn-secondary"
            @click="emitSecondaryClick"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ImageField } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import { RichText, Image, Field, ComponentRendering, Link } from '@sitecore-jss/sitecore-jss-vue';
import { computed, onMounted, ref } from 'vue';
import { usePageStore } from '@/store';
import { LinkField } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';
import { isMobileDevice } from '@/utils/dom-helpers';
import { downloadAppleMembershipCard, downloadGoogleMembershipCard } from '@/constants/forms-api.const';
import { useFeatureTogglesStore } from '@/store/feature-toggles/featureTogglesStore';
import { platform } from '@/utils/device-helper';

export interface IAppBannerProps {
  title?: string;
  subTitle?: string;
  icon?: ImageField;
  greyBg?: boolean;
  purpleBg?: boolean;
  appleDownloadLink?: string;
  appleDownloadLinkDesktop?: string;
  googleDownloadLink?: string;
  googleDownloadLinkDesktop?: string;
  showAppStoreButtons?: boolean;
  showPrimaryButton?: boolean;
  showSecondaryButton?: boolean;
  fields?: Record<string, Field<string | boolean> | ImageField | LinkField>;
  rendering?: ComponentRendering;
  addMembershipCardDesktopLink?: LinkField;
  showAddToWalletLink?: boolean;
}

const title = computed(() => (props.fields?.title?.value as string) ?? props.title);
const subTitle = computed(() => (props.fields?.subTitle?.value as string) ?? props.subTitle);
const icon = computed(() => (props.fields?.icon as ImageField) ?? props.icon);
const greyBg = computed(() => (props.fields?.greyBg?.value as boolean) ?? props.greyBg);
const purpleBg = computed(() => (props.fields?.purpleBg?.value as boolean) ?? props.purpleBg);
const devicePlatform = computed(() => platform());
const showAddToWallet = computed(
  () => (props.fields?.showAddToWalletLink?.value as boolean) ?? props.showAddToWalletLink
);
const addToWalletLinkDesktop = computed(
  () => props.fields?.addMembershipCardDesktopLink ?? props.addMembershipCardDesktopLink
);

const appleDownloadLink = computed(() => {
  const desktopLink = (props.fields?.appleDownloadLinkDesktop?.value as string) ?? props.appleDownloadLinkDesktop;
  if (!desktopLink || isMobileDevice())
    return (props.fields?.appleDownloadLink?.value as string) ?? props.appleDownloadLink;
  return desktopLink;
});
const googleDownloadLink = computed(() => {
  const desktopLink = (props.fields?.googleDownloadLinkDesktop?.value as string) ?? props.googleDownloadLinkDesktop;
  if (!desktopLink || isMobileDevice())
    return (props.fields?.googleDownloadLink?.value as string) ?? props.googleDownloadLink;
  return desktopLink;
});
const showAppStoreButtons = computed(
  () => (props.fields?.showAppStoreButtons?.value as boolean) ?? props.showAppStoreButtons
);
const primaryBtn = computed(() => props.fields?.primaryButton as LinkField);
const secondaryBtn = computed(() => props.fields?.secondaryButton as LinkField);
const showPrimaryBtn = computed(() => props.fields?.showPrimaryButton?.value ?? props.showPrimaryButton);
const showSecondaryBtn = computed(() => props.fields?.showSecondaryButton?.value ?? props.showPrimaryButton);
const bgClass = computed(() => {
  if (purpleBg.value) return 'bg-racq-light-purple';
  return greyBg.value ? 'bg-racq-anti-flash-white' : 'bg-racq-info-light-blue';
});

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = defineProps<IAppBannerProps>();

const featureToggleStore = useFeatureTogglesStore();
const outerClass = props.rendering?.params?.Styles;
const isGoogleWalletEnabled = ref(false);
const isAppleWalletEnabled = ref(false);
const inlineStyle = props.rendering?.params?.inlineStyle;
const dataTestId = usePageStore().dataTestId(props.rendering);
const iconMap = {
  appleWalletIcon: require('@/assets/icons/apple_wallet.svg'),
  googleWalletIcon: require('@/assets/icons/google_wallet.svg'),
};
const emit = defineEmits(['buttonClick']);

const inlineBanner = computed(() => !subTitle.value);
const stackedBanner = computed(() => subTitle.value);

onMounted(async () => {
  isAppleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardAppleWallet');
  isGoogleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardGoogleWallet');
});

const emitPrimaryClick = (e: MouseEvent) => {
  if (isEmptyLink(primaryBtn.value?.value?.href)) e.preventDefault();
  emit('buttonClick', 'primaryAppBannerButton');
};

const emitSecondaryClick = (e: MouseEvent) => {
  if (isEmptyLink(secondaryBtn.value?.value?.href)) e.preventDefault();
  emit('buttonClick', 'secondaryAppBannerButton');
};

function handleWalletClick(walletPlatform: 'apple' | 'android') {
  if (walletPlatform === 'android') {
    window.location.href = downloadGoogleMembershipCard;
  } else if (walletPlatform === 'apple') {
    alert('Redirecting to Apple Wallet');
    window.location.href = downloadAppleMembershipCard;
  } else {
    alert('Wallet not supported on this device.');
  }
}

const isEmptyLink = (href: string | undefined) => !href || href === 'http://';
</script>
